"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import useT from '@hooks/useTranslation'
import mix from '@utils/styles/mix'
import { NOTICE_MS, subscribeNotice, takePendingNotice } from 'lib/notice/notice'

import styles from './Notice.module.css'

const RESUME_MIN_MS = 1500
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])'

// Komunikat po akcji: jeden naraz, nad dolną nawigacją; tekst w regionie live, fokus zostaje na elemencie,
// który go wywołał. Najechanie i fokus wstrzymują odliczanie; Esc i „Zamknij” oddają fokus.
export default function Notice () {
    const [notice, setNotice] = useState(null)
    const [text, setText] = useState('')
    const [on, setOn] = useState(false)
    const rootRef = useRef(null)
    const actionRef = useRef(null)
    const closeRef = useRef(null)
    const timer = useRef({ id: null, left: 0, start: 0, hover: false, focus: false })
    const t = { close: useT('Zamknij komunikat') }

    const hide = useCallback((returnFocus) => {
        const root = rootRef.current
        const inside = root?.contains(document.activeElement)
        clearTimeout(timer.current.id)
        timer.current.id = null
        setOn(false)
        const opener = notice?.opener
        if ((returnFocus || inside) && opener?.isConnected) opener.focus()
    }, [notice])

    const start = useCallback((ms) => {
        clearTimeout(timer.current.id)
        timer.current.left = ms
        timer.current.start = Date.now()
        timer.current.id = setTimeout(() => hide(false), ms)
    }, [hide])

    const pause = () => {
        if (!timer.current.id) return
        clearTimeout(timer.current.id)
        timer.current.id = null
        timer.current.left = Math.max(timer.current.left - (Date.now() - timer.current.start), 0)
    }

    const resume = () => {
        const s = timer.current
        if (on && !s.id && !s.hover && !s.focus) start(Math.max(s.left, RESUME_MIN_MS))
    }

    const show = useCallback((next) => {
        setNotice(next)
        setText('')
        setOn(true)
    }, [])

    useEffect(() => subscribeNotice(show), [show])

    const pathname = usePathname()
    useEffect(() => {
        const pending = takePendingNotice()
        if (pending) show({ ...pending, at: Date.now() })
    }, [pathname, show])

    // tekst wpisany po chwili: ten sam komunikat drugi raz też zostanie odczytany
    useEffect(() => {
        if (!notice) return
        const id = setTimeout(() => setText(notice.message), 100)
        const root = rootRef.current
        timer.current.hover = Boolean(root?.matches(':hover'))
        timer.current.focus = Boolean(root?.contains(document.activeElement))
        const ms = notice.ms || NOTICE_MS
        timer.current.left = ms
        if (!timer.current.hover && !timer.current.focus) start(ms)
        return () => clearTimeout(id)
    }, [notice, start])

    useEffect(() => () => clearTimeout(timer.current.id), [])

    // klawiatura: Tab z elementu wywołującego wchodzi do komunikatu, Shift+Tab wraca, Tab z ostatniego idzie dalej; Esc zamyka
    useEffect(() => {
        if (!on) return
        const onKey = (e) => {
            const root = rootRef.current
            const opener = notice?.opener
            const active = document.activeElement
            const inside = root?.contains(active)
            if (e.key === 'Escape' && (inside || active === opener)) {
                e.preventDefault()
                e.stopPropagation()
                hide(true)
                return
            }
            if (e.key !== 'Tab' || !opener) return
            const items = [actionRef.current, closeRef.current].filter(Boolean)
            if (active === opener && !e.shiftKey) {
                e.preventDefault()
                items[0]?.focus()
            } else if (inside && e.shiftKey && active === items[0]) {
                e.preventDefault()
                opener.focus()
            } else if (inside && !e.shiftKey && active === items[items.length - 1]) {
                e.preventDefault()
                const all = [...document.querySelectorAll(FOCUSABLE)]
                    .filter(el => !root.contains(el) && el.getClientRects().length > 0 && !el.closest('[inert]'))
                all[all.indexOf(opener) + 1]?.focus()
            }
        }
        document.addEventListener('keydown', onKey, true)
        return () => document.removeEventListener('keydown', onKey, true)
    }, [on, notice, hide])

    const action = notice?.action

    return (
        <div
            className={mix([styles.notice, notice?.stack && styles.stack, on && styles.on])}
            data-notice=""
            data-on-dark=""
            onBlur={(e) => { if (!rootRef.current?.contains(e.relatedTarget)) { timer.current.focus = false; resume() } }}
            onFocus={() => { timer.current.focus = true; pause() }}
            onMouseEnter={() => { timer.current.hover = true; pause() }}
            onMouseLeave={() => { timer.current.hover = false; resume() }}
            ref={rootRef}
        >
            <p aria-live="polite" className={styles.text} role="status">{text}</p>
            <div className={styles.act}>
                { action && !action.href && action.onClick && (
                    <button
                        aria-label={action.name}
                        className={styles.btn}
                        onClick={() => { hide(false); action.onClick() }}
                        ref={actionRef}
                        type="button"
                    >
                        {action.label}
                    </button>
                ) }
                { action?.href && (
                    <a
                        aria-label={action.name}
                        className={action.button ? styles.btn : styles.link}
                        href={action.href}
                        onClick={action.onClick}
                        ref={actionRef}
                    >
                        {action.label}
                    </a>
                ) }
                <button aria-label={t.close} className={styles.close} onClick={() => hide(true)} ref={closeRef} type="button">
                    <SvgIcon name="close" />
                </button>
            </div>
        </div>
    )
}
